import { paginaRecordada, recordarPagina, olvidarPagina, fijarCampusPagina } from "../../../../utils/paginaRecordada";
import React, {
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import { useLocation, useNavigate } from "react-router-dom";
import {
    useDispatch,
    useSelector
} from "react-redux";

import {
    FINANCES_MODALS
} from "../../../../constants";
import {
    RootState,
    enableIdleLockSwitch,
    disableIdleLockSwitch,
    displayNotice,
    AppDispatch,
    setCurrentUserCampus,
    setCurrentUserCampusId
} from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import {
    setCurrentCampus,
    setCurrentCampusId,
    setPermissions,
    UserState
} from "../../../../redux/reducers/sessionReducer";
import { setPaymentMethods, setRecipientAccounts } from "../../../../redux/reducers/catalogReducer";
import {
    handlePaymentXlsx,
    handleSelectedPayment,
    setIsCancelPayment,
    setIsNewInvoice,
    setIsPartialPayment,
    setModalContent,
    setRefundItems,
    setSelectedInvoice,
    setSelectedPayment,
    setShowModal,
    setStudentData,
    setTotalToPaid
} from "../../../../redux/reducers/paymentsReducer";
import { handleSelectedInvoice, setSelectedCredit } from "../../../../redux/reducers/invoicesReducer";
import PaymentView, { TotalesPagos } from "./paymentView";
import { handleSelectedCredit } from "../../../../redux/reducers/creditsReducer";
import { getErrorMessage } from "../../../../common";
import useEnlaceDirecto from "../../../../utils/enlaceDirecto";
import useEsTelefono from "../../../../utils/esTelefono";
import { buildPages } from "../../managment/enrollmentRequests/helpers";
import { tituloEstado } from "../../../../utils/tituloEstado";
import { criterioDe, fechasDe, FILTROS_INICIALES, FiltrosPagos, OpcionesPagos, reportePermitido } from "./filtrosPagos";

/*
    Pagos (rehecho 2026-11-04, ver paymentView). Los filtros viven en UN objeto (FiltrosPagos) y el criterio que va al servidor
    se arma de él en un solo lugar (criterioDe): la lista, los totales y el Excel salen SIEMPRE del mismo criterio. Antes había
    tres fuentes (chips, embudos por columna y buscador) que se pisaban, y el Excel podía salir de toda la historia.
*/

// Los filtros se recuerdan por plantel mientras la pestaña siga abierta (al volver de un perfil o estado de cuenta).
const LLAVE_FILTROS = (campusId: number) => `wapp.pagos.filtros.${campusId}`;
const leerFiltros = (campusId: number): FiltrosPagos => {
    try {
        const g = JSON.parse(sessionStorage.getItem(LLAVE_FILTROS(campusId)) || "null");
        return g && typeof g === "object" ? { ...FILTROS_INICIALES, ...g } : FILTROS_INICIALES;
    } catch {
        return FILTROS_INICIALES;
    }
};
const guardarFiltros = (campusId: number, f: FiltrosPagos) => {
    try { sessionStorage.setItem(LLAVE_FILTROS(campusId), JSON.stringify(f)); } catch { /* sin almacenamiento: no pasa nada */ }
};

const PaymentController = (props: any, route: any) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();
    const movil = useEsTelefono();
    const pageSizes: number[] = [100, 200, 50];   // 100 por página por defecto (Esteban, 2026-09-11)
    const http = HttpManager.getInstance();
    const idleLocked: boolean = useSelector((state: RootState) => state.idleLockSwitch.value);
    const userData: UserState | null = useSelector((state: RootState) => state.rootReducer.sessionState.user);
    const currentRole: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentRole);
    const currentCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
    const campuses: any[] = useSelector((state: RootState) => state.rootReducer.catalog.campuses);
    const selectedInvoice: any = useSelector((state: RootState) => state.rootReducer.payments.selectedInvoice);
    const selectedPayment: any = useSelector((state: RootState) => state.rootReducer.payments.selectedPayment);
    const isPartialPayment: boolean = useSelector((state: RootState) => state.rootReducer.payments.isPartialPayment);
    const isNewInvoice: boolean = useSelector((state: RootState) => state.rootReducer.payments.isNewInvoice);
    const paymentStatuses: any[] = useSelector((state: RootState) => state.rootReducer.catalog.paymentStatuses) || [];
    const campusId: number = currentCampus?.id ?? 0;

    const [filas, setFilas] = useState<any[] | null>(null);
    const [totales, setTotales] = useState<TotalesPagos | null>(null);
    const [cargando, setCargando] = useState<boolean>(false);
    const [pagina, setPagina] = useState<number>(() => { fijarCampusPagina(currentCampus?.id); return paginaRecordada() || 1; });
    const [ultimaPagina, setUltimaPagina] = useState<number>(1);
    const [itemCount, setItemCount] = useState<{ start: number; end: number; total: number }>({ start: 0, end: 0, total: 0 });
    const [porPagina, setPorPagina] = useState<number>(pageSizes[0]);
    const [filtros, setFiltrosState] = useState<FiltrosPagos>(() => leerFiltros(campusId));
    const [busqueda, setBusqueda] = useState<string>("");
    const [busquedaAplicada, setBusquedaAplicada] = useState<string>("");
    // Pago que pidió un enlace (?data=… del reporte o de un aviso): manda sobre los filtros hasta que se toquen.
    const [enlazado, setEnlazado] = useState<number | null>(null);
    const [opciones, setOpciones] = useState<OpcionesPagos>({ concepts: [], methods: [], accounts: [], statuses: [], creators: [] });
    const pedidoRef = useRef<number>(0);
    const abrirEnlazadoRef = useRef<boolean>(false);
    const campusCargadoRef = useRef<number>(0);

    const criterio = useMemo(() => enlazado ? { id: enlazado } : criterioDe(filtros, busquedaAplicada), [filtros, busquedaAplicada, enlazado]);
    // «Hoy» cambia a medianoche aunque el objeto de filtros sea el mismo: se compara por texto.
    const llaveCriterio = JSON.stringify(criterio);

    const setFiltros = (f: FiltrosPagos) => {
        setEnlazado(null);
        setPagina(1);
        olvidarPagina();
        setFiltrosState(f);
        if (campusId) guardarFiltros(campusId, f);
    };

    const onChangeSearch = (v: string) => setBusqueda(v);
    const onClickSearch = () => { setEnlazado(null); setPagina(1); olvidarPagina(); setBusquedaAplicada(busqueda); };
    const onClearSearch = () => { setBusqueda(""); setEnlazado(null); setPagina(1); olvidarPagina(); setBusquedaAplicada(""); clearQueryParams(); };

    /*
        Pagos y Movimientos (Esteban, 2026-09-22): la fila "Pago" es lo que entró al banco; debajo, con sangría, van sus
        movimientos (uno por alumno). Las filas de pago se insertan aquí: el servidor manda los movimientos ya ordenados.
    */
    const conFilasDePago = (rows: any[]): any[] => {
        const out: any[] = [];
        let ultimo: string | null = null;
        (rows || []).forEach((p: any) => {
            if (p.isMulti && p.receiptNumber !== ultimo) {
                out.push({
                    isReceiptRow: true, isMulti: true, id: Number(p.receiptNumber), receiptNumber: p.receiptNumber, receiptTotal: p.receiptTotal,
                    movementCount: p.movementCount, personCount: Number(p.personCount || 1), payerName: p.payerName,
                    statusId: p.receiptCancelled ? 3 : 1, status: p.receiptCancelled ? "Anulado" : "Aprobado",
                    currencyCode: p.currencyCode, paymentMethod: p.paymentMethod, recipientAccount: p.recipientAccount, reference: p.receiptReference || p.reference,
                    paymentDate: p.paymentDate, createdAtDateTime: p.createdAtDateTime, createdAt: p.createdAt, notes: "",
                    receiptMoney: (p.currencyCode || "") + Number(p.receiptTotal || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                });
            }
            ultimo = p.isMulti ? p.receiptNumber : null;
            out.push(p);
        });
        return out;
    };
    const [receiptDetailId, setReceiptDetailId] = useState<number | null>(null);
    const [showMulti, setShowMulti] = useState<boolean>(false);
    // Descuentos especiales: Supervisión de Caja llega por el enlace del aviso de Cliq (/payments?descuento=N&plantel=X).
    const [discountsOpen, setDiscountsOpen] = useState<boolean>(false);
    const [discountFocus, setDiscountFocus] = useState<number | null>(null);
    const enlace = useEnlaceDirecto("descuento", (id) => { setDiscountFocus(id); setDiscountsOpen(true); });

    const fetchRoleData = async () => {
        try {
            const response = await http.roleService.getRoleData(currentRole.id);
            dispatch(setPermissions(response.data.permissions));
            return response.data;
        } catch (err: any) {
            console.error(err);
        }
    }

    const resetStateData = () => {
        dispatch(setRefundItems(new Set()));
        dispatch(setIsPartialPayment(false));
    }

    const onError = (message: string) => {
        dispatch(displayNotice({
            cornerClose: false,
            message: message,
            heading: <h3 style={{
                color: "var(--wa-rojo-error)",
                display: "inline-block"
            }}>
                Error
            </h3>
        }));
    }

    const getStudentById = async (studentId: number) => {
        try {
            let response = await http.studentService.getStudentById(
                currentCampus.id,
                studentId
            );
            let student = response.data;
            return student;
        } catch (error) {
            console.log(error);
            onError(getErrorMessage(error));
        }
    }

    const setSelectedStudent = async (data: any) => {
        const studentData = await getStudentById(data.studentId);
        dispatch(setStudentData(studentData));
    }

    const getPaymentMethods = async () => {
        try {
            const response = await http.paymentMethodService
                .getPaymentMethodsByCountry(currentCampus.countryId);
            const paymentMethods = response.data;
            return paymentMethods;
        } catch (err: any) {
            console.error(err);
        }
    }

    const getRecipientAccounts = async () => {
        try {
            const response = await http.recipientAccountService
                .getRecipientAccounts(currentCampus.countryId);
            const accounts = response.data;
            return accounts;
        } catch (err: any) {
            console.error(err);
        }
    }


    /*
        El alumno sale de la fila que se abrió (o de la factura abierta), no de studentData: ese llega después y en la siguiente
        apertura todavía es el del alumno anterior (pedía …/students/undefined/… o la factura con otro alumno). Sin alumno no se pide.
    */
    const getInvoiceById = async (invoiceId: number, studentId?: number | null) => {
        const alumno = Number(studentId || (selectedInvoice && Number(selectedInvoice.id) === Number(invoiceId) ? selectedInvoice.studentId : 0) || 0);
        if (!invoiceId || !alumno) {
            return;
        }
        try {
            dispatch(enableIdleLockSwitch());
            let response = await dispatch(handleSelectedInvoice({
                sessionToken: userData?.token ?? "",
                campusId: currentCampus.id,
                studentId: alumno,
                invoiceId
            })).unwrap();
            let invoice = response.data;
            setSelectedStudent(invoice);
            dispatch(setSelectedInvoice(invoice));
            dispatch(setTotalToPaid(invoice.pendingAmount));
            dispatch(setIsNewInvoice(false));
            dispatch(setShowModal(true));
        } catch (error) {
            onError("Hubo un error al tratar de obtener el documento!");
        }
        finally {
            dispatch(disableIdleLockSwitch());
        }
    }

    const showPaymentDetail = async (rowData: any) => {
        try {
            dispatch(enableIdleLockSwitch());
            const response = await dispatch(handleSelectedPayment({
                sessionToken: userData?.token ?? "",
                campusId: currentCampus.id,
                paymentId: rowData.id
            })).unwrap();
            const payment = {
                ...response.data,
                account: response.data.recipientAccount,
                amount: response.data.amount.value,
                files: response.data.documents
            };
            setSelectedStudent(payment);
            dispatch(setSelectedPayment(payment));
            dispatch(setModalContent(FINANCES_MODALS.PAYMENT_DETAIL));
            dispatch(setShowModal(true));
        } catch (error) {
            onError("Hubo un error al tratar de obtener el documento!");
        }
        finally {
            dispatch(disableIdleLockSwitch());
        }
    }

    const showCreditDetail = async (rowData: any) => {
        try {
            dispatch(enableIdleLockSwitch());
            let response = await dispatch(handleSelectedCredit({
                campusId: currentCampus.id,
                creditId: rowData.id
            })).unwrap();
            const credit = response.data;
            dispatch(setSelectedCredit(credit));
            dispatch(setModalContent(FINANCES_MODALS.APPLY_CREDIT_DETAILS));
            dispatch(setShowModal(true));
        } catch (error) {
            onError("Hubo un error al tratar de obtener el documento!");
        }
        finally {
            dispatch(disableIdleLockSwitch());
        }
    }

    const cancelInvoice = () => {
        dispatch(setModalContent(FINANCES_MODALS.CANCEL_INVOICE));
    }

    const proccessPayment = () => {
        dispatch(setIsPartialPayment(true));
        dispatch(setIsNewInvoice(false));
        dispatch(setModalContent(FINANCES_MODALS.EMIT_PAYMENT));
    }

    const closeModalInvoiceDetail = () => {
        dispatch(setIsPartialPayment(false));
        dispatch(setShowModal(false));
        dispatch(setSelectedInvoice(null));
    }

    const handleCancelPayment = () => {
        setIsCancelPayment(true);
        dispatch(setModalContent(FINANCES_MODALS.CANCEL_PAYMENT));
    }

    const paymentDetailCloseModal = () => {
        dispatch(setShowModal(false));
    }

    const showPaymentUpdateModal = () => {
        dispatch(setModalContent(FINANCES_MODALS.UPDATE_PAYMENT));
    }

    const updatePaymentCloseModal = () => {
        dispatch(setModalContent(FINANCES_MODALS.PAYMENT_DETAIL))
    }

    const onSuccessUpdatePayment = () => {
        dispatch(setModalContent(FINANCES_MODALS.PAYMENT_DETAIL));
        showSuccessMessage();
        showPaymentDetail(selectedPayment);
    }

    const showSuccessMessage = () => {
        dispatch(displayNotice({
            cornerClose: false,
            message: "Información procesada con éxito",
            heading: <h3 style={{
                color: "var(--wa-verde)", display: "inline-block"
            }}>
                Listo
            </h3>
        }));
    }

    const cancelPaymentCloseModal = () => {
        setIsCancelPayment(false);
        dispatch(setModalContent(FINANCES_MODALS.PAYMENT_DETAIL));
    }

    const onSuccessCancelPayment = () => {
        fetchData();
        if (selectedInvoice) {
            getInvoiceById(selectedInvoice.id, selectedInvoice.studentId);
        }
        dispatch(setShowModal(true));
        showPaymentDetail(selectedPayment);
        showSuccessMessage();
    }

    const cancelEmitPayment = () => {
        if (isPartialPayment && !isNewInvoice) {
            dispatch(setModalContent(FINANCES_MODALS.INVOICE));
        } else {
            dispatch(setModalContent(FINANCES_MODALS.CHECKOUT));
        }
    }

    const showApplyCreditModal = () => {
        dispatch(setModalContent(FINANCES_MODALS.APPLY_CREDIT));
    }

    const onSuccessEmitPayment = () => {
        fetchData();
        getInvoiceById(selectedInvoice.id, selectedInvoice.studentId);
        dispatch(setShowModal(true));
        dispatch(displayNotice({
            cornerClose: false,
            message: "Información procesada con éxito",
            heading: <h3 style={{
                color: "var(--wa-verde)", display: "inline-block"
            }}>
                Listo
            </h3>
        }));
    }

    const closeModalEmitPayment = () => {
        dispatch(setShowModal(false));
    }

    const cancelInvoiceCloseModal = () => {
        dispatch(setModalContent(FINANCES_MODALS.INVOICE));
    }

    const onSuccessCancelInvoice = () => {
        fetchData();
        dispatch(setShowModal(false));
        dispatch(displayNotice({
            cornerClose: false,
            message: "Información procesada con éxito",
            heading: <h3 style={{
                color: "var(--wa-verde)", display: "inline-block"
            }}>
                Listo
            </h3>
        }))
    }

    const generateInvoiceCloseModal = () => {
        dispatch(setModalContent(FINANCES_MODALS.CHECKOUT));
    }

    const onSuccessGeneratePaidInvoice = () => {
        fetchData();
        dispatch(setShowModal(false));
        showSuccessMessage();
    }

    const getQueryParams = () => {
        const query = new URLSearchParams(location.search);
        const data = query.get("data");
        if (!data) {
            return { campusId: 0, invoiceId: 0 };
        }

        try {
            const decodedData = JSON.parse(decodeURIComponent(data));
            return {
                campusId: decodedData?.campus ?? 0,
                paymentId: decodedData?.payment ?? 0
            };
        } catch (error) {
            console.error("Error al parsear JSON de query params:", error);
            return { campusId: 0, invoiceId: 0 };
        }
    }

    const clearQueryParams = () => {
        const query = new URLSearchParams(location.search);

        query.forEach((value, key) => {
            query.delete(key);  // delete each parameter
        });

        window.history.replaceState(
            null,
            '',
            `${location.pathname}${location.hash}`  // holding hash, but delete all query params
        );
    }

    const handleCampusChange = (campusId: number) => {
        localStorage.setItem("currentCampus", `C_${campusId}`);
        dispatch(setCurrentUserCampusId(campusId));
        dispatch(setCurrentUserCampus(`C_${campusId}`));
        dispatch(setCurrentCampusId(campusId));
        const newCampus = campuses.find((campus: any) => campus.id === campusId);
        //Sin catálogo (llegó por el puente de app.washingtonacademy.mx) no se pisa el plantel con un vacío:
        //app.tsx pide el catálogo y lo sincroniza (2026-10-21).
        if (newCampus) {
            dispatch(setCurrentCampus(newCampus));
        }
    }

    /* La lista y sus totales, del criterio actual. Una respuesta vieja nunca pisa a una nueva. */
    const recargar = async (paginaPedida: number = pagina) => {
        if (!campusId || !userData) return;
        const turno = ++pedidoRef.current;
        setCargando(true);
        try {
            const response = await http.paymentService.getPaymentsByCampusId(userData?.token ?? "", campusId, porPagina, criterio, paginaPedida);
            if (turno !== pedidoRef.current) return;
            const r = response.data;
            setFilas(conFilasDePago(r.data || []));
            setTotales(r.totals || null);
            setUltimaPagina(Math.max(1, Number(r.last_page || 1)));
            setItemCount({ start: Number(r.from || 0), end: Number(r.to || 0), total: Number(r.total || 0) });
            recordarPagina(Number(r.current_page || paginaPedida));
            // Página recordada que ya no existe (se borraron pagos o cambió el filtro): a la última que sí.
            if (Number(r.last_page || 1) < paginaPedida && paginaPedida > 1) {
                setPagina(Math.max(1, Number(r.last_page || 1)));
            }
            if (abrirEnlazadoRef.current && enlazado) {
                abrirEnlazadoRef.current = false;
                const renglon = (r.data || []).find((p: any) => Number(p.id) === Number(enlazado));
                if (renglon) {
                    showPaymentDetail(renglon);
                } else {
                    onError("El pago del enlace ya no está disponible en este plantel.");
                }
            }
        } catch (error: any) {
            if (turno !== pedidoRef.current) return;
            if (error?.status === 404 || error?.response?.status === 404) {
                navigate("/");
                return;
            }
            setFilas([]);
            onError("Ocurrió un error al obtener los pagos.");
        } finally {
            if (turno === pedidoRef.current) setCargando(false);
        }
    };
    const fetchData = () => { resetStateData(); recargar(); };
    const refrescarPagos = () => recargar();

    /* Opciones de los filtros: método y cuenta del país, estatus del catálogo, concepto y quién registra del servidor. */
    const cargarOpciones = async () => {
        if (!currentCampus) return;
        const [metodos, cuentas, extra] = await Promise.all([
            getPaymentMethods(),
            getRecipientAccounts(),
            http.paymentService.getPaymentFilterOptions(campusId).then((r: any) => r.data).catch(() => null)
        ]);
        if (metodos) dispatch(setPaymentMethods(metodos));
        if (cuentas) dispatch(setRecipientAccounts(cuentas));
        setOpciones({
            methods: (metodos || []).map((m: any) => ({ id: Number(m.id), name: m.name })),
            accounts: (cuentas || []).map((c: any) => ({ id: Number(c.id), name: c.name })),
            statuses: (paymentStatuses || []).map((s: any) => ({ id: Number(s.id), name: tituloEstado(s.name) })),
            concepts: (extra?.concepts || []).map((c: any) => ({ id: String(c.id), name: tituloEstado(String(c.name).replace(/Cuota de inscripci[oó]n/i, "Inscripción").replace(/Cuota de anulaci[oó]n/i, "Anulación")) })),
            creators: (extra?.creators || []).map((u: any) => ({ id: Number(u.id), name: tituloEstado(String(u.name).toLowerCase()) }))
        });
    };

    const handleDownloadReport = async () => {
        if (!reportePermitido(criterio)) {
            // No es un error: falta un paso. Sin fechas el Excel sería toda la historia (Esteban, 2026-11-04).
            dispatch(displayNotice({
                cornerClose: false,
                message: "El reporte sale de las fechas que elijas arriba (Hoy, Esta Semana, Este Mes o un Rango).",
                heading: <h3 style={{ color: "var(--wa-tinta)", display: "inline-block" }}>Elige las Fechas</h3>
            }));
            return;
        }
        const { from, to } = fechasDe(filtros);
        const plantel = currentCampus?.name ? " " + currentCampus.name : "";
        const rango = enlazado ? ` pago ${enlazado}` : from && to ? (from === to ? ` ${from}` : ` ${from} al ${to}`) : "";
        try {
            dispatch(enableIdleLockSwitch());
            await dispatch(handlePaymentXlsx({
                campusId,
                criteria: criterio,
                fileName: `Pagos${plantel}${rango}.xlsx`
            })).unwrap();
        } catch (error) {
            onError("Hubo un error al tratar de obtener el documento!");
        }
        finally {
            dispatch(disableIdleLockSwitch());
        }
    }

    useEffect(() => {
        fetchRoleData();
    }, [currentRole]);

    useEffect(() => {
        if (selectedInvoice && Object.keys(selectedInvoice).length > 0) {
            dispatch(setShowModal(true));
            dispatch(setModalContent(FINANCES_MODALS.INVOICE));
        }
    }, [selectedInvoice]);

    // Cambio de plantel: sus propios filtros recordados y sus opciones.
    useEffect(() => {
        if (!userData?.token || !currentCampus) {
            if (userData?.token && currentCampus == null) {
                onError(
                    "No hay planteles registrados o asignados al rol en uso por el usuario, " +
                    "por lo que el acceso a este módulo permanecerá deshabilitado."
                );
            }
            return;
        }
        const { campusId: campusEnlace, paymentId } = getQueryParams() as any;
        if (campusEnlace > 0 && paymentId > 0 && campusEnlace !== campusId) {
            handleCampusChange(campusEnlace);
            return;
        }
        if (campusEnlace > 0 && paymentId > 0) {
            //El enlace manda: lista con ese pago y su ficha abierta (reporte de pagos, Esteban 2026-09-12).
            olvidarPagina();
            setPagina(1);
            setEnlazado(Number(paymentId));
            abrirEnlazadoRef.current = true;
            clearQueryParams();
        }
        fijarCampusPagina(campusId);
        if (campusCargadoRef.current && campusCargadoRef.current !== campusId) {
            setFiltrosState(leerFiltros(campusId));
            setPagina(1);
        }
        campusCargadoRef.current = campusId;
        cargarOpciones();
    }, [userData?.token, campusId]);

    useEffect(() => {
        if (!userData?.token || !campusId) return;
        recargar(pagina);
    }, [userData?.token, campusId, llaveCriterio, porPagina, pagina]);

    const pages = useMemo(() => buildPages(pagina, ultimaPagina, (n) => { if (n >= 1 && n <= ultimaPagina) setPagina(n); }, movil), [pagina, ultimaPagina, movil]);

    return currentCampus == null || userData === null
        ? <React.Fragment></React.Fragment>
        : <PaymentView
            multiProps={{
                show: showMulti,
                open: () => setShowMulti(true),
                close: () => setShowMulti(false),
                onSuccess: (receipt: any) => { setShowMulti(false); refrescarPagos(); setReceiptDetailId(Number(receipt.id)); },
                campusId: currentCampus?.id,
                countryId: currentCampus?.countryId,
                currencySymbol: currentCampus?.currency || "",
                discounts: { open: discountsOpen, focusId: discountFocus, show: () => { setDiscountFocus(null); setDiscountsOpen(true); }, close: () => setDiscountsOpen(false) },
                avisoEnlace: { texto: enlace.aviso, cerrar: enlace.cerrar }
            }}
            receiptProps={{
                id: receiptDetailId,
                close: () => setReceiptDetailId(null),
                onCancelled: () => refrescarPagos(),
                onOpenMovement: (paymentId: number) => { setReceiptDetailId(null); showPaymentDetail({ id: paymentId }); },
                campusId: currentCampus?.id
            }}
            listProps={{
                movil,
                filas,
                totales,
                cargando,
                bloqueado: idleLocked,
                sym: (filas || []).find((r: any) => r.currencyCode)?.currencyCode || currentCampus?.currency || "",
                filtros,
                opciones,
                onFiltros: setFiltros,
                onReporte: handleDownloadReport,
                enlazado,
                quitarEnlace: () => { setEnlazado(null); setPagina(1); },
                onAbrir: (r: any) => r.isReceiptRow ? setReceiptDetailId(r.id) : showPaymentDetail(r),
                onFactura: (invoiceId: number, studentId: number) => getInvoiceById(invoiceId, studentId)
            }}
            paginationProps={{
                pages,
                pageSizes,
                itemCount,
                onPageSize: (n: number) => { setPorPagina(n); setPagina(1); olvidarPagina(); }
            }}
            searchProps={{
                search: busqueda,
                onChangeSearch,
                onClickSearch,
                onClearSearch
            }}
            invoiceDetailProps={{
                cancelInvoice,
                showPaymentDetail,
                showCreditDetail,
                proccessPayment,
                closeModal: closeModalInvoiceDetail
            }}
            emitPaymentProps={{
                isNewInvoice,
                isPartialPayment,
                closeModal: closeModalEmitPayment,
                cancelEmitPayment,
                showApplyCreditModal,
                onSuccess: onSuccessEmitPayment
            }}
            paymentDetailProps={{
                onReceiptCancelled: () => refrescarPagos(),
                handleCancelPayment,
                closeModal: paymentDetailCloseModal,
                showPaymentUpdate: showPaymentUpdateModal
            }}
            paymentUpdateProps={{
                onSuccess: onSuccessUpdatePayment,
                closeModal: updatePaymentCloseModal
            }}
            cancelPaymentProps={{
                closeModal: cancelPaymentCloseModal,
                onSuccess: onSuccessCancelPayment
            }}
            cancelInvoiceProps={{
                closeModal: cancelInvoiceCloseModal,
                onSuccess: onSuccessCancelInvoice
            }}
            generateInvoiceProps={{
                onSuccess: onSuccessGeneratePaidInvoice,
                closeModal: generateInvoiceCloseModal
            }}
        />
}

export default PaymentController;
